import { useState, type ChangeEvent } from 'react';
import {
  Alert, Box, Button, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth/AuthContext';
import { useTransakcjeRestauracji } from '@/lib/useAnalytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PANEL RESTAURACJI → TRANSAKCJE. Historia sprzedanych porcji.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ┌─ CZYM TA TABELA NIE JEST ───────────────────────────────────────────────────────────────────┐
 * │ NIE jest wyciągiem bankowym ani rozliczeniem. Ziarnem jest POZYCJA ZAMÓWIENIA, a suma to    │
 * │ suma cen katalogowych tych pozycji — nie kwota, którą klient faktycznie zapłacił i nie      │
 * │ kwota, którą restauracja dostanie. Rabaty, korekty i zwroty żyją w rozliczeniach, nie tu.   │
 * │                                                                                             │
 * │ Dlatego nagłówek kolumny mówi „wartość katalogowa", a nie „kwota", a pod podsumowaniem      │
 * │ stoi zdanie o podstawie przysłane przez serwer. Liczba bez podstawy zostanie przeczytana    │
 * │ jako przychód — i rozjedzie się z księgowością dokładnie wtedy, gdy ktoś na niej polegnie.  │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Bez danych osobowych kupującego. To nie jest luka w parytecie ze starym panelem
 * administracyjnym: administrator widzi nazwisko, bo odpowiada na reklamacje — restauracja
 * rozlicza się z posiłków, a nie z osób.
 */

/** Grosze → złote. Formatowanie polskie, bo tu czyta człowiek, a nie eksport do arkusza. */
function zlote(grosze: number | null | undefined): string {
  if (grosze === null || grosze === undefined) return '—';
  return (grosze / 100).toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
}

/** Data jak w reszcie panelu: dzień, bez sekund — nikt nie planuje kuchni co do sekundy. */
function dzien(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString('pl-PL');
}

export function TransakcjeRestauracjiPage() {
  const auth = useAuth();
  const czyWlasciciel = auth.has('admin.users.read');

  const [restauracja, setRestauracja] = useState('');
  const [od, setOd] = useState('');
  const [doo, setDoo] = useState('');
  const [strona, setStrona] = useState(1);

  const wskazana = restauracja.trim() === '' ? undefined : Number(restauracja);
  const brakWskazania = czyWlasciciel && wskazana === undefined;

  const { data, isLoading, error, isFetching } = useTransakcjeRestauracji({
    restauracja: wskazana,
    od: od || undefined,
    do: doo || undefined,
    strona,
  });

  const wiersze = data?.wiersze ?? [];
  const rozmiar = data?.rozmiar_strony ?? 25;
  const ogolem = data?.wierszy_ogolem ?? 0;
  const stron = Math.max(1, Math.ceil(ogolem / rozmiar));

  /* Zmiana filtra MUSI cofnąć na pierwszą stronę. Inaczej po zawężeniu zakresu ekran zostaje
     na stronie 7, której w nowym wyniku nie ma, i pokazuje pustkę zamiast danych. */
  function zFiltrem(ustaw: (v: string) => void) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      ustaw(e.target.value);
      setStrona(1);
    };
  }

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Historia transakcji"
        opis="Sprzedane pozycje: co, do której szkoły i w jakiej ilości. Historia, nie rozliczenie."
      />

      <Karta>
        <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {czyWlasciciel && (
            <TextField
              label="Restauracja (nr)" size="small" sx={{ width: 180 }} value={restauracja}
              onChange={zFiltrem(setRestauracja)}
              helperText="Konto właściciela nie ma własnej restauracji"
            />
          )}
          <TextField
            label="Od" type="date" size="small" value={od} onChange={zFiltrem(setOd)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Do" type="date" size="small" value={doo} onChange={zFiltrem(setDoo)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Karta>

      {brakWskazania && (
        <EmptyState
          title="Wskaż restaurację"
          description="Konto właściciela nie jest przypisane do restauracji, więc trzeba podać, czyj panel oglądasz."
        />
      )}

      {!brakWskazania && isLoading && <LoadingState />}
      {!brakWskazania && error ? <ErrorState error={error} context="Panel restauracji — transakcje." /> : null}

      {!brakWskazania && !isLoading && !error && wiersze.length === 0 && (
        <EmptyState
          title="Brak transakcji w tym zakresie"
          description="Nie ma sprzedanych pozycji w wybranym przedziale dat. Spróbuj szerszego zakresu."
        />
      )}

      {wiersze.length > 0 && (
        <Karta>
          <TableContainer
            component={Paper}
            elevation={0}
            /* Przyciemnienie w trakcie dociągania kolejnej strony: tabela nie miga pustką,
               ale też nie udaje, że pokazuje już nowe dane. */
            sx={{ overflowX: 'auto', opacity: isFetching ? 0.6 : 1, transition: 'opacity 120ms' }}
          >
            <Table size="small" aria-label="Historia transakcji restauracji">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Dostawa</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Szkoła</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Danie</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ilość</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Wartość katalogowa
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wiersze.map((w) => (
                  <TableRow key={w.pozycja_id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>
                      {dzien(w.data)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'var(--krój-data)' }}>
                      {dzien(w.data_dostawy)}
                    </TableCell>
                    <TableCell>{w.szkola ?? '—'}</TableCell>
                    <TableCell>{w.danie ?? '—'}</TableCell>
                    <TableCell align="right">{w.ilosc ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{zlote(w.cena_grosze)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            spacing={2}
            sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="body2">
                <strong>{data?.podsumowanie.sztuk ?? 0}</strong> szt. ·{' '}
                <strong>{zlote(data?.podsumowanie.suma_grosze)}</strong>
              </Typography>
              {/* Zdanie o podstawie idzie z serwera i jest pokazywane DOSŁOWNIE. */}
              <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
                {data?.podsumowanie.podstawa}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button size="small" disabled={strona <= 1} onClick={() => setStrona((s) => s - 1)}>
                Poprzednia
              </Button>
              <Typography variant="body2">
                {strona} / {stron}
              </Typography>
              <Button size="small" disabled={strona >= stron} onClick={() => setStrona((s) => s + 1)}>
                Następna
              </Button>
            </Stack>
          </Stack>
        </Karta>
      )}

      <Alert severity="info">
        Suma jest <strong>wartością katalogową pozycji</strong>, a nie kwotą pobraną od klienta
        ani kwotą do wypłaty. Rabaty, korekty i zwroty rozlicza się poza tym ekranem.
      </Alert>
    </Stack>
  );
}

export default TransakcjeRestauracjiPage;
